import { ResultTypeFrom } from '@reduxjs/toolkit/query';
import { LegacyDataProviderRowData } from '@adv-frontend/data-provider';
import { useCallback, useMemo } from 'react';
import { mapSubRows } from './lib/mapSubRows/mapSubRows';
import { BaseRowData, UseEndpointDataProviderRowsGenerator } from './types';
import { AnyQueryDefinition } from '../apiSSRModule';
import { iterateOverChunks } from './lib/iterateOverChunks/iterateOverChunks';
import { useLoadedRows } from './lib/useLoadedRows/useLoadedRows';

/**
 * Параметры конфигурации статического генератора строк датапровайдера.
 */
type StaticRowsGeneratorConfig<Definition extends AnyQueryDefinition> = {
    /** Функция получения общего количества строк из ответа эндпоинта */
    getTotalCount(chunkData: ResultTypeFrom<Definition>): number;
};

/**
 * Создает статический генератор строк датапровайдера.
 *
 * @param config Параметры конфигурации генератора строк
 * @returns Генератор строк датапровайдера
 */
export function createStaticRowsGenerator<
    Definition extends AnyQueryDefinition,
    RowData extends BaseRowData,
    Params extends object,
>({
    getTotalCount: getTotalCountOption,
}: StaticRowsGeneratorConfig<Definition>): UseEndpointDataProviderRowsGenerator<Definition, RowData, Params> {
    return function useRowsGenerator(paginator, { getRowByIndex, cachedValues, requestedChunks, initialData }) {
        const chunkLength = paginator.getLimit();
        const totalCount = initialData ? getTotalCountOption(initialData) : 0;

        const getLoadedRows = useLoadedRows(requestedChunks, cachedValues, getRowByIndex, chunkLength);

        const getRows = useCallback(() => {
            /**
             * Формируем список всех строк в провайдере.
             * Строка может быть в одном из четырех состояний:
             * 1. Строка загружена – есть id, status = 'loaded'
             * 2. Строка не загружена, но ее можно выделить – есть id, status = 'not-loaded'
             * 3. Строка не загружена и ее нельзя выделить – вместо id `not_loaded_{index}`, status = 'not-loaded'
             * 4. Строка загружена, но данные для нее не получены – вместо id `load-failed_{index}`, status = 'load-failed'
             */

            // Формируем список всех строк в незагруженном состоянии.
            const result: LegacyDataProviderRowData[] = Array(totalCount)
                .fill(null)
                .map((_, index) => ({ id: `not_loaded_${index}`, status: 'not-loaded' }));

            const loadedIds: string[] = [];

            // Заполняем список загруженными строками.
            // Загруженные строки не обязательно идут друг за другом,
            // так как чанки могут загружаться не последовательно.
            iterateOverChunks(
                requestedChunks,
                cachedValues,
                (rowData, requestedChunk, rowIndexInChunk) => {
                    loadedIds.push(rowData.id);
                    result[requestedChunk.startIndex + rowIndexInChunk] = {
                        id: rowData.id,
                        status: 'loaded',
                        ...(rowData?.subRows && { subRows: mapSubRows(rowData.subRows) }),
                    };
                },
                {
                    chunkLength,
                    getRowByIndex,
                },
                (requestedChunk, rowIndexInChunk) => {
                    /**
                     * Проверяем существование строки в списке строк, полученных по totalCount
                     * Если она есть, значит мы запрашивали (ожидаем получить) для нее данные, но нам не удалось их загрузить,
                     * поэтому помечаем такую строку как ошибочно загруженную
                     */
                    if (result[requestedChunk.startIndex + rowIndexInChunk]) {
                        result[requestedChunk.startIndex + rowIndexInChunk] = {
                            id: `load-failed_${requestedChunk.startIndex + rowIndexInChunk}`,
                            status: 'load-failed',
                        };
                    }
                }
            );

            return result;
        }, [cachedValues, requestedChunks, getRowByIndex, chunkLength, totalCount]);

        const getTotalCount = useCallback(() => totalCount, [totalCount]);

        return useMemo(
            () => ({
                getRows,
                getLoadedRows,
                getTotalCount,
            }),
            [getRows, getLoadedRows, getTotalCount]
        );
    };
}
